import type { RoomService } from '@features/room';
import type { PublisherStats } from '@shared/types';
import type {
  StreamPublisherPort,
  StreamPublisherSession,
  StreamPublisherState,
  StreamPublisherStats,
} from '../ports/StreamPublisherPort';

/**
 * StreamPublisherPort adapter that runs on top of an already-connected
 * RoomService. Does NOT own a LiveKit Room — the room feature is the single
 * owner of the underlying connection.
 *
 * Lifecycle:
 *   - start()        → if RoomService is not connected, throw (caller must join first).
 *                       Otherwise republish the local stream on the existing room.
 *   - stop()         → unpublish all tracks, keep the room connected.
 *   - Publisher stats are forwarded from RoomService.onPublisherStatsChanged.
 */
export class RoomBackedStreamPublisher implements StreamPublisherPort {
  private state: StreamPublisherState = 'idle';
  private session: StreamPublisherSession | null = null;
  private stats: StreamPublisherStats = {};

  private readonly stateListeners = new Set<(state: StreamPublisherState) => void>();
  private readonly statsListeners = new Set<(stats: StreamPublisherStats) => void>();
  private readonly remoteStreamListeners = new Set<(stream: MediaStream) => void>();
  private readonly remoteStream = new MediaStream();

  private unsubscribeStats: (() => void) | null = null;

  constructor(
    private readonly roomService: RoomService,
    /**
     * If a room is not connected when `start()` is called, the publisher
     * delegates to this hook to bring one up. The hook is responsible for
     * choosing a room name and marking the room as `joinedForStreamingOnly`
     * so a later teardown knows it can leave without disturbing co-hosts.
     */
    private readonly ensureRoom: () => Promise<string>,
  ) {}

  public async start(localStream: MediaStream): Promise<void> {
    if (this.state === 'starting' || this.state === 'streaming') {
      return;
    }

    if (!this.roomService.isConnected()) {
      await this.ensureRoom();

      if (!this.roomService.isConnected()) {
        throw new Error(
          'Cannot start streaming: room failed to connect automatically.',
        );
      }
    }

    this.setState('starting');

    try {
      this.subscribeStatsOnce();

      await this.roomService.publishTracks(localStream);

      this.session = {
        localStream,
        remoteStream: this.remoteStream,
      };
      this.emitRemoteStream(this.remoteStream);

      this.setState('streaming');
    } catch (error) {
      this.teardownStatsSubscription();
      this.session = null;
      this.setState('error');
      throw error;
    }
  }

  public async stop(): Promise<void> {
    if (this.state === 'idle' || this.state === 'stopping') {
      return;
    }

    this.setState('stopping');
    try {
      await this.roomService.unpublishAllTracks();
    } finally {
      this.teardownStatsSubscription();
      this.session = null;
      this.stats = {};
      this.setState('idle');
    }
  }

  public getState(): StreamPublisherState {
    return this.state;
  }

  public getSession(): StreamPublisherSession | null {
    return this.session;
  }

  public getStats(): StreamPublisherStats {
    return this.stats;
  }

  public onStateChanged(listener: (state: StreamPublisherState) => void): () => void {
    this.stateListeners.add(listener);
    listener(this.state);
    return () => {
      this.stateListeners.delete(listener);
    };
  }

  public onStatsChanged(listener: (stats: StreamPublisherStats) => void): () => void {
    this.statsListeners.add(listener);
    if (Object.keys(this.stats).length > 0) {
      listener(this.stats);
    }
    return () => {
      this.statsListeners.delete(listener);
    };
  }

  public onRemoteStream(listener: (stream: MediaStream) => void): () => void {
    this.remoteStreamListeners.add(listener);
    if (this.remoteStream.getTracks().length > 0) {
      listener(this.remoteStream);
    }
    return () => {
      this.remoteStreamListeners.delete(listener);
    };
  }

  private subscribeStatsOnce(): void {
    if (this.unsubscribeStats) {
      return;
    }
    this.unsubscribeStats = this.roomService.onPublisherStatsChanged((stats) => {
      this.stats = this.mapStats(stats);
      this.emitStats(this.stats);
    });
  }

  private teardownStatsSubscription(): void {
    this.unsubscribeStats?.();
    this.unsubscribeStats = null;
  }

  private mapStats(stats: PublisherStats): StreamPublisherStats {
    return {
      bitrateKbps: stats.bitrateKbps,
      fps: stats.fps,
      latencyMs: stats.latencyMs,
      codec: stats.codec ?? undefined,
      packetsSent: stats.packetsSent,
      packetsReceived: stats.packetsReceived,
    };
  }

  private setState(state: StreamPublisherState): void {
    this.state = state;
    for (const listener of this.stateListeners) {
      listener(state);
    }
  }

  private emitStats(stats: StreamPublisherStats): void {
    for (const listener of this.statsListeners) {
      listener(stats);
    }
  }

  private emitRemoteStream(stream: MediaStream): void {
    for (const listener of this.remoteStreamListeners) {
      listener(stream);
    }
  }
}
